import { useEffect, useState } from 'react';
import { getFamilyList } from '../../services/family';
import { getMembershipSubscription, isMembershipSubscriptionNotFound } from '../services/Payments';
import { MembershipSubscription } from '../types/Payments';
import { Relative } from '../types/Relative';
import { GREETINGS_SUMMARY_CACHE_PREFIX } from '../utils/greetingsSummaryCache';

const GREETINGS_PLAN_KEY = 'greetingsAddon';

// The server answers a Daily Greetings lookup with the subscription it found. If it ever hands back a
// different plan (an older server that ignores planKey), that answer says nothing about Greetings.
export const isGreetingsAddonSubscription = (subscription: MembershipSubscription | null | undefined): boolean =>
    Boolean(subscription) && subscription?.planKey === GREETINGS_PLAN_KEY;

// A Daily Greetings subscription in any of these statuses is live: it keeps its Greetings page and
// Home tile through a billing grace period. These are the backend's ACTIVE_MEMBERSHIP_SUBSCRIPTION_STATUSES
// (PaymentRepository.js) without 'pending_activation': the server does not send Greetings for a first
// payment that has not gone through, so that resident sees sign-up, not the full page. 'canceled' and
// anything unknown is not live either.
const GREETINGS_ADDON_LIVE_STATUSES = [
    'active',
    'past_due',
    'payment_action_required',
    'unpaid',
];

export const isGreetingsAddonActive = (subscription: MembershipSubscription | null | undefined): boolean =>
    isGreetingsAddonSubscription(subscription)
    && GREETINGS_ADDON_LIVE_STATUSES.includes(String(subscription?.status || '').toLowerCase());

export interface GreetingsRecipient {
    id: string;
    // Empty when the entry came from the cache, which keeps initials only.
    name: string;
    initials: string;
}

export interface GreetingsSummary {
    // 'idle' = nothing to show yet or Greetings is not offered; 'loading' = looking it up;
    // 'failed' = could not tell. Callers must treat 'idle' and 'failed' like 'loading' and show
    // nothing extra, never an upsell.
    status: 'idle' | 'loading' | 'ready' | 'failed';
    // Has a live Daily Greetings subscription (including one waiting to cancel at period end).
    subscribed: boolean;
    // That subscription is set to end at period end; Greetings is already switched off.
    cancelAtPeriodEnd: boolean;
    recipients: GreetingsRecipient[];
}

const IDLE: GreetingsSummary = { status: 'idle', subscribed: false, cancelAtPeriodEnd: false, recipients: [] };
const LOADING: GreetingsSummary = { ...IDLE, status: 'loading' };
const FAILED: GreetingsSummary = { ...IDLE, status: 'failed' };

const initialsOf = (name: string): string =>
    name
        .split(' ')
        .map((part) => part[0] || '')
        .join('')
        .slice(0, 2)
        .toUpperCase();

// The last answer per resident, kept in the browser so a reload draws the right tile at once and
// the lookups only confirm it. It holds the yes/no and the avatar initials -- never contact names.
interface CachedSummary {
    subscribed: boolean;
    cancelAtPeriodEnd: boolean;
    recipients: { id: string; initials: string }[];
}

const cacheKey = (residentId: string, facilityId: string) => `${GREETINGS_SUMMARY_CACHE_PREFIX}${facilityId}.${residentId}`;

const readCache = (residentId?: string, facilityId?: string): GreetingsSummary | null => {
    if (!residentId || !facilityId) return null;
    try {
        const raw = window.localStorage.getItem(cacheKey(residentId, facilityId));
        if (!raw) return null;
        const cached = JSON.parse(raw) as CachedSummary;
        if (typeof cached?.subscribed !== 'boolean' || !Array.isArray(cached.recipients)) return null;
        return {
            status: 'ready',
            subscribed: cached.subscribed,
            cancelAtPeriodEnd: Boolean(cached.cancelAtPeriodEnd),
            recipients: cached.recipients.map((r) => ({ id: String(r.id), name: '', initials: String(r.initials || '') })),
        };
    } catch (_error) {
        return null;
    }
};

const writeCache = (residentId: string, facilityId: string, summary: GreetingsSummary) => {
    try {
        const cached: CachedSummary = {
            subscribed: summary.subscribed,
            cancelAtPeriodEnd: summary.cancelAtPeriodEnd,
            recipients: summary.recipients.map((r) => ({ id: r.id, initials: r.initials })),
        };
        window.localStorage.setItem(cacheKey(residentId, facilityId), JSON.stringify(cached));
    } catch (_error) {
        // Private mode or a full store: the tile just waits for the lookups.
    }
};

// Call after the resident signs up or cancels, so the Home tile never draws the old answer first.
export const clearGreetingsSummaryCache = (residentId?: string, facilityId?: string) => {
    if (!residentId || !facilityId) return;
    try {
        window.localStorage.removeItem(cacheKey(residentId, facilityId));
    } catch (_error) {
        // Nothing to clear.
    }
};

// Enrolment and recipient list for the Hello! tile on the home page. Recipients are
// filtered like the Greetings page and the backend delivery filter: active contacts
// with greetingRecipient on, excluding the resident's own self-management row.
//
// `enabled`: true = look it up; false = Greetings is not sold here (guest, or the facility has card
// payments off): no lookups, plain tile; undefined = the facility answer is still on its way, so
// show whatever the cache says and wait.
const useGreetingsSummary = (residentId?: string, facilityId?: string, enabled?: boolean): GreetingsSummary => {
    const [summary, setSummary] = useState<GreetingsSummary>(() => readCache(residentId, facilityId) || IDLE);

    useEffect(() => {
        if (!residentId || !facilityId || enabled === false) {
            if (enabled === false) clearGreetingsSummaryCache(residentId, facilityId);
            setSummary(IDLE);
            return undefined;
        }
        const cached = readCache(residentId, facilityId);
        if (enabled === undefined) {
            setSummary(cached || IDLE);
            return undefined;
        }
        let cancelled = false;
        setSummary(cached || LOADING);
        (async () => {
            try {
                // The subscription first: the family list is only needed for a live Greetings subscription,
                // so a resident without one never makes that call.
                let subscription: MembershipSubscription | null = null;
                try {
                    subscription = await getMembershipSubscription(residentId, facilityId, GREETINGS_PLAN_KEY);
                } catch (error) {
                    if (!isMembershipSubscriptionNotFound(error)) throw error;
                }
                // A different plan than asked for is not an answer about Greetings: could not tell.
                if (subscription && !isGreetingsAddonSubscription(subscription)) {
                    if (!cancelled) setSummary(FAILED);
                    return;
                }
                if (!isGreetingsAddonActive(subscription)) {
                    const unsubscribed: GreetingsSummary = { ...IDLE, status: 'ready' };
                    writeCache(residentId, facilityId, unsubscribed);
                    if (!cancelled) setSummary(unsubscribed);
                    return;
                }
                const family = (await getFamilyList(residentId)) as { relatives?: Relative[] };
                const recipients = (family?.relatives || [])
                    .filter((r) => r.isActive === true && r.managerType !== 'Resident' && r.greetingRecipient === true)
                    .map((r) => {
                        const name = `${r.FirstName || ''} ${r.LastName || ''}`.trim() || 'Contact';
                        return { id: String(r._id), name, initials: initialsOf(name) };
                    });
                const subscribed: GreetingsSummary = {
                    status: 'ready',
                    subscribed: true,
                    cancelAtPeriodEnd: Boolean(subscription?.cancelAtPeriodEnd),
                    recipients,
                };
                writeCache(residentId, facilityId, subscribed);
                if (!cancelled) setSummary(subscribed);
            } catch (_error) {
                if (!cancelled) setSummary(FAILED);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [residentId, facilityId, enabled]);

    return summary;
};

export default useGreetingsSummary;
